import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, List, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { useConfirm } from '@/hooks/useConfirm';

const StaffConfig = () => {
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    phone: '', 
    email: '', 
    role: 'operaria',
    services_assigned: [],
    commissions: []
  });
  const [viewMode, setViewMode] = useState('list');
  const { confirm, ConfirmDialog } = useConfirm();
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [staffRes, servicesRes] = await Promise.all([api.getStaff(), api.getServices()]);
      setStaff(staffRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      toast.error('Error al cargar personal');
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.role) {
      toast.error('Nombre y Rol son obligatorios');
      return;
    }
    
    try {
      // Prepare data: send null for empty optional fields instead of empty strings
      const dataToSend = {
        name: formData.name,
        role: formData.role,
        phone: formData.phone?.trim() || null,
        email: formData.email?.trim() || null,
        services_assigned: formData.services_assigned || [],
        commissions: formData.commissions || []
      };
      
      console.log('Sending staff data:', dataToSend);
      const response = editingItem 
        ? await api.updateStaff(editingItem.id, dataToSend)
        : await api.createStaff(dataToSend);
      
      console.log('Server response:', response.data);
      toast.success(editingItem ? 'Personal actualizado' : 'Personal creado');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error details:', error);
      console.error('Error response:', error.response);
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map(err => 
            `${err.loc.join('.')}: ${err.msg}`
          ).join(', ');
          toast.error(`Error de validación: ${errorMessages}`);
        } else {
          toast.error(`Error: ${error.response.data.detail}`);
        }
      } else if (error.response?.status === 422) {
        toast.error('Error de validación: Por favor revisa los campos del formulario');
      } else {
        toast.error('Error al guardar personal. Por favor intenta nuevamente.');
      }
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ 
      name: '', 
      phone: '', 
      email: '', 
      role: 'operaria',
      services_assigned: [],
      commissions: []
    });
  };

  const toggleService = (serviceId) => {
    const current = formData.services_assigned || [];
    const newServices = current.includes(serviceId)
      ? current.filter(id => id !== serviceId)
      : [...current, serviceId];
    setFormData({ ...formData, services_assigned: newServices });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Equipo</h2>
        <div className="flex space-x-2">
          <div className="flex border border-[#C9A961]/30 rounded">
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'bg-[#C9A961]' : ''}>
              <List size={16} />
            </Button>
            <Button variant={viewMode === 'cards' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('cards')} className={viewMode === 'cards' ? 'bg-[#C9A961]' : ''}>
              <LayoutGrid size={16} />
            </Button>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#C9A961]">
            <Plus className="mr-2" size={18} /> Nuevo
          </Button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="border border-[#C9A961]/20 rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Servicios</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staff.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">{member.name}</TableCell>
                  <TableCell className="capitalize">{member.role}</TableCell>
                  <TableCell>{member.phone || '-'}</TableCell>
                  <TableCell>{member.email || '-'}</TableCell>
                  <TableCell>
                    <span className="px-2 py-1 text-xs rounded bg-[#C9A961]/10 text-[#C9A961]">
                      {member.services_assigned?.length || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button size="sm" variant="outline" onClick={() => { 
                        setEditingItem(member); 
                        setFormData({
                          name: member.name,
                          phone: member.phone || '',
                          email: member.email || '',
                          role: member.role,
                          services_assigned: member.services_assigned || [],
                          commissions: member.commissions || []
                        }); 
                        setShowModal(true); 
                      }}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500" onClick={async () => {
                        const confirmed = await confirm('Eliminar integrante', '¿Estás seguro de eliminar este integrante? Esta acción no se puede deshacer.');
                        if (confirmed) {
                          await api.deleteStaff(member.id);
                          loadData();
                        }
                      }}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {staff.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-[#6B6B6B] py-8">No hay personal registrado</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {staff.map((member) => (
          <Card key={member.id} className="p-4">
            <h3 className="font-semibold text-lg">{member.name}</h3>
            <p className="text-sm text-[#6B6B6B] capitalize">{member.role}</p>
            {member.phone && <p className="text-sm text-[#6B6B6B]">{member.phone}</p>}
            {member.email && <p className="text-sm text-[#6B6B6B] truncate">{member.email}</p>}
            {member.services_assigned && member.services_assigned.length > 0 && (
              <p className="text-xs text-[#C9A961] mt-2">{member.services_assigned.length} servicios asignados</p>
            )}
            <div className="flex space-x-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => { 
                setEditingItem(member); 
                setFormData({
                  name: member.name,
                  phone: member.phone || '',
                  email: member.email || '',
                  role: member.role,
                  services_assigned: member.services_assigned || [],
                  commissions: member.commissions || []
                }); 
                setShowModal(true); 
              }}>
                <Edit size={16} />
              </Button>
              <Button size="sm" variant="outline" className="text-red-500" onClick={async () => {
                const confirmed = await confirm('Eliminar integrante', '¿Estás seguro de eliminar este integrante? Esta acción no se puede deshacer.');
                if (confirmed) {
                  await api.deleteStaff(member.id);
                  loadData();
                }
              }}>
                <Trash2 size={16} />
              </Button>
            </div>
          </Card>
        ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#C9A961]">{editingItem ? 'Editar' : 'Nuevo'} Integrante</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required placeholder="Nombre completo" />
              </div>
              <div className="space-y-2">
                <Label>Rol *</Label>
                <Input value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} required placeholder="Ej: operaria, masajista" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="099 123 456" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="email@ejemplo.com" />
              </div>
            </div>
            
            {services.length > 0 && (
              <div style={{ marginTop: '32px' }}>
                <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '0px' }}>
                  Servicios Asignados
                </div>
                {/* Espacio agregado: 40px */}
                <div style={{ height: '40px' }}></div>
                <div className="border border-[#C9A961]/30 rounded-lg space-y-3 max-h-48 overflow-y-auto" style={{ padding: '16px' }}>
                  {services.map(service => (
                    <div key={service.id} className="flex items-center" style={{ gap: '16px' }}>
                      <Checkbox
                        checked={(formData.services_assigned || []).includes(service.id)}
                        onCheckedChange={() => toggleService(service.id)}
                        id={`service-${service.id}`}
                      />
                      <Label htmlFor={`service-${service.id}`} className="cursor-pointer text-sm font-normal">
                        {service.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</Button>
              <Button type="submit" className="bg-[#C9A961]">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default StaffConfig;